import { Fragment, useEffect, useState, useMemo } from "react";
import deepClone from "../../utils/deepClone";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { GROUPING_VIEW_TYPE, DRIVE_FOLDER_SEARCH_TYPE, UNCATEGORIZED_ID, TABLE_LIST_TYPE } from "@constants";

import { setNotification } from "@modules/notification";
import {
    resetSelectedFolder,
    initFolderData,
    updateFolderSearchState,
    recoverFolderData,
    searchFolderData,
    updateSelectedFolder,
    resetFolderTreeView,
} from "@modules/folderTreeView";
import {
    clear,
    updateFilter,
    initCategoryData,
    resetSelectedCategory,
    changeSelectedItem,
} from "@modules/categoryTreeView";
import { update } from "@modules/assets";

import compareVersions, {
    VERSION_CM_11210_ASSET_DUPLICATION,
    VERSION_CM_12200_CREATE_COLLECTION_WITH_ASSET_FOLDER,
    VERSION_CM_12290_COLLECTION_ARCHIVE,
    VERSION_CM_8339_FOLDER_VIEW,
} from "@cores/version";
import FolderTreeView from "@components/tree/folder-tree-view";
import TreeSearchBar from "@components/tree/tree-searchbar";
import VersionedComponent from "@components/VersionedComponent";
import AssetListViewSelectBtn from "@routes/asset/ui/AssetListViewSelectBtn";
import { IconCategory, IconFolder, IconChevronRight, IconChevronLeft } from "@mzc-cloudplex/icons";
import menuService from "@services/menuService";
import { clear as clearCategoryTree } from "../../modules/categoryTreeView";
import { Box, Button, ButtonGroup, Collapse, Divider, Grid, IconButton, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";

import { useMediaQuery } from "@mui/material";
import { ViewSelector } from "@components/viewSelector";
import CategoryTreeView from "@components/tree/category-tree-view";
import ListViewSelectButton from "@components_v2/list-view-select-button/list-view-select-button";
import useCheckPermission from "@hooks/useCheckPermission";
import { siteMode } from "@cores/siteMode";
import CollapsibleSplitTwoTierGridRight from "@components_v2/grid/collapsible-split-two-tier-grid-left";

const TreeViewLayout = ({
    resourceType,
    driveDomain,
    driveInfo,
    activeView,
    searchQueryParams,
    abortSource,
    popperMenu,
    isCustomMenu,
    onClickForderTree,
    onClickTreeItem,
    onClickCreateNewFolder,
    onRefresh,
    updateActiveView,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);
    const isCms = siteMode.get() === "CMS";

    const FOLDERS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Folders" });
    const CATEGORIES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Categories" });

    const stage = useSelector(({ stage }) => stage);
    const categoryTreeView = useSelector(({ categoryTreeView }) => categoryTreeView);
    const folderTreeView = useSelector(({ folderTreeView }) => folderTreeView);

    const [search, setSearch] = useState("");
    const [isFold, setIsFold] = useState(false);
    const isDown1024 = useMediaQuery(theme.breakpoints.down("lg"));
    const isUp1024 = useMediaQuery(theme.breakpoints.up("lg"));

    const enableFolderView = useMemo(() => {
        if (searchQueryParams.params?.hasOwnProperty("enableFolderView")) {
            return searchQueryParams.params?.enableFolderView;
        }
        return true;
    }, [searchQueryParams]);

    const enableCategoryView = useMemo(() => {
        if (searchQueryParams.params?.hasOwnProperty("enableCategoryView")) {
            return searchQueryParams.params?.enableCategoryView;
        }
        return true;
    }, [searchQueryParams]);

    useEffect(() => {
        if (isDown1024) {
            return setIsFold(true);
        }
    }, [isDown1024]);

    useEffect(() => {
        if (isUp1024) {
            return setIsFold(false);
        }
    }, [isUp1024]);

    const useFolderView = useMemo(() => {
        return (
            compareVersions(stage.version, VERSION_CM_8339_FOLDER_VIEW) >= 0 &&
            [TABLE_LIST_TYPE.ASSET, "Archived", TABLE_LIST_TYPE.COLLECTION, TABLE_LIST_TYPE.CUSTOM_CONTENT].includes(
                resourceType,
            )
        );
    }, [resourceType, stage]);

    const defaultGroupingType = useMemo(() => (useFolderView ? "folder" : "category"), [useFolderView]);

    const [groupingType, setGroupingType] = useState(defaultGroupingType);

    const initRootVirtualFolder = async () => {
        const initFolder = async () => {
            const headerParams = { projectId };
            const rootFolder = await dispatch(
                initFolderData({
                    headerParams,
                    domain: driveDomain,
                    resourceTypeId: driveInfo?.resourceTypeId,
                }),
            );
            await onClickForderTree(rootFolder, false);
        };

        try {
            if (searchQueryParams.params.virtualFolderId && searchQueryParams.params.virtualDriveId) {
                try {
                    const params = {
                        headerParams: { projectId },
                        driveId: searchQueryParams.params.virtualDriveId,
                        queryParameters: {
                            id: searchQueryParams.params.virtualFolderId,
                            searchType: DRIVE_FOLDER_SEARCH_TYPE.PARENT_FOLDERS,
                        },
                        abortSignal: abortSource?.token,
                        domain: driveDomain,
                        resourceTypeId: driveInfo?.resourceTypeId,
                    };
                    const selectedFolder = await dispatch(recoverFolderData(params));
                    dispatch(updateSelectedFolder(selectedFolder));
                } catch (error) {
                    dispatch(
                        setNotification({
                            type: "info",
                            contents: t(`common::msg::${error?.message}`, error?.message),
                        }),
                    );
                    await initFolder();
                }
            } else {
                await initFolder();
            }
        } catch (error) {
            console.error(error);
            dispatch(
                setNotification({
                    type: "error",
                    contents: error.response
                        ? `[Error:${error.response.status}] => ${error.response?.data?.message}`
                        : `Internal Error Occured`,
                }),
            );
        }
    };

    const initRootCategories = async () => {
        if (categoryTreeView.pending) return;

        await getCategoryData(projectId, resourceType);

        if (searchQueryParams.params?.selectedCategoryId) {
            dispatch(
                changeSelectedItem({
                    id: searchQueryParams.params.selectedCategoryId,
                }),
            );
        } else {
            onClickTreeItem({
                id: UNCATEGORIZED_ID,
                hierarchyType: GROUPING_VIEW_TYPE.CATEGORY,
            });
        }
    };

    const onChangeSearchValue = (search) => {
        setSearch(search);
    };

    const onHandleClickRefresh = async () => {
        try {
            setSearch("");

            const headerParams = {
                projectId,
            };
            const rootFolder = await dispatch(
                initFolderData({
                    headerParams,
                    domain: driveDomain,
                    resourceTypeId: driveInfo?.resourceTypeId,
                }),
            );
            onClickForderTree(rootFolder, false);

            dispatch(
                updateFolderSearchState({
                    isSearch: false,
                }),
            );
        } catch (error) {
            console.error(error);
        }
    };

    const onClickCategoryTreeFilter = () => {
        const keyword = search;

        if (!keyword) return;

        const filteredData = deepClone(categoryTreeView.data).filter(function f(o) {
            o.isFold = false;
            o.isEmpty = true;

            if (o.name.toUpperCase().includes(keyword.toUpperCase())) {
                o.isEmpty = false;
                o.isFold = true;
                return true;
            }
            return o.children && (o.children = o.children.filter(f)).length > 0;
        });

        dispatch(
            updateFilter({
                isFilter: true,
                data: filteredData || [],
            }),
        );
    };

    const onClickFolder = async (item, isClickArrow) => {
        try {
            if (folderTreeView.isSearch) {
                const params = {
                    headerParams: { projectId },
                    driveId: item.driveId,
                    queryParameters: {
                        id: item.id,
                        searchType: DRIVE_FOLDER_SEARCH_TYPE.PARENT_FOLDERS,
                    },
                    abortSignal: abortSource?.token,
                    domain: driveDomain,
                    resourceTypeId: driveInfo?.resourceTypeId,
                };
                await dispatch(recoverFolderData(params));

                dispatch(
                    updateFolderSearchState({
                        isSearch: false,
                    }),
                );

                setSearch("");
            }
            onClickForderTree(item, isClickArrow);
        } catch (error) {
            console.error(error);
        }
    };

    const onClickFolderTreeFilter = async () => {
        try {
            const keyword = search;

            if (!keyword) return;

            const queryParameters = {
                name: keyword,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.NAME,
            };

            const headerParams = { projectId };
            await dispatch(
                searchFolderData({
                    headerParams,
                    queryParameters,
                    abortSignal: abortSource?.token,
                    domain: driveDomain,
                    resourceTypeId: driveInfo?.resourceTypeId,
                }),
            );

            await dispatch(
                updateFolderSearchState({
                    isSearch: true,
                }),
            );
        } catch (error) {
            console.log(error);
        }
    };

    const onClickResetCategoryTreeFilter = () => {
        dispatch(
            updateFilter({
                isFilter: false,
                data: [],
            }),
        );

        setSearch("");
    };

    const getCategoryData = async (projectId, resourceType) => {
        try {
            const response = await dispatch(initCategoryData(projectId, resourceType));
            return response;
        } catch {
            return null;
        }
    };

    const onClickGroupingTypeTab = async (type) => {
        setGroupingType(type);
        setSearch("");

        if (type === GROUPING_VIEW_TYPE.CATEGORY) {
            abortSource?.cancel();
            await getCategoryData(projectId, resourceType);
            await dispatch(resetSelectedFolder());
            onClickTreeItem({
                id: UNCATEGORIZED_ID,
                hierarchyType: GROUPING_VIEW_TYPE.CATEGORY,
            });
        } else {
            let rootFolder;

            if (folderTreeView.driveList.data?.length > 0) {
                rootFolder =
                    folderTreeView.folderList.data[
                        folderTreeView.driveList.data?.find((el) => el.resourceType?.type === driveDomain.toUpperCase())
                            ?.rootFolder?.id
                    ];
            } else {
                try {
                    const headerParams = {
                        projectId,
                    };
                    rootFolder = await dispatch(
                        initFolderData({
                            headerParams,
                            domain: driveDomain,
                            resourceTypeId: driveInfo?.resourceTypeId,
                        }),
                    );
                } catch (error) {
                    console.error(error);
                    dispatch(
                        setNotification({
                            type: "error",
                            contents: error.response
                                ? `[Error:${error.response.status}] => ${error.response?.data?.message}`
                                : `Internal Error Occured`,
                        }),
                    );
                }
            }

            await dispatch(resetSelectedCategory());
            onClickForderTree(rootFolder);
        }
        dispatch(update({ hierarchyType: type }));
    };

    useEffect(() => {
        setGroupingType(searchQueryParams.params?.hierarchyType ?? defaultGroupingType);

        if (
            ![TABLE_LIST_TYPE.ASSET, "Archived", TABLE_LIST_TYPE.COLLECTION, TABLE_LIST_TYPE.CUSTOM_CONTENT].includes(
                resourceType,
            ) ||
            searchQueryParams.params?.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY ||
            compareVersions(stage.version, VERSION_CM_8339_FOLDER_VIEW) < 0
        ) {
            initRootCategories();
            //CategoryTreeViewActions.changeSelectedItem(response.categoryMap[UNCATEGORIZED_ID]);
        } else {
            initRootVirtualFolder();
        }

        return () => {
            dispatch(resetFolderTreeView());
            dispatch(clearCategoryTree());
            abortSource?.cancel();
        };
    }, [driveInfo?.resourceTypeId]);

    useEffect(() => {
        if (!categoryTreeView.data || !projectId) return;
        dispatch(clear());
    }, [projectId]);

    return (
        <Fragment>
            <Box
                sx={{
                    position: `relative`,
                    height: `100%`,
                    backgroundColor: theme.palette.common.white,
                    [theme.breakpoints.down("lg")]: {
                        position: `absolute`,
                        zIndex: theme.zIndex.drawer,
                    },
                }}
            >
                <Grid container flexDirection={"column"} sx={{ height: `100%` }}>
                    <Grid item>
                        {useFolderView ? (
                            <Grid container flexWrap={"nowrap"} py={1.5}>
                                {/* 뷰 타입 변경 버튼 */}
                                {resourceType !== "Archived" && (
                                    <ListViewSelectButton
                                        domain={resourceType}
                                        isActiveView={activeView}
                                        updateActiveView={updateActiveView}
                                    />
                                )}
                                {/* //뷰 타입 변경 버튼 */}
                                <ButtonGroup size={"small"} sx={{ width: `100%` }}>
                                    <VersionedComponent version={VERSION_CM_8339_FOLDER_VIEW}>
                                        {enableFolderView && (
                                            <Button
                                                fullWidth
                                                variant={
                                                    groupingType === GROUPING_VIEW_TYPE.FOLDER
                                                        ? "contained"
                                                        : "outlined"
                                                }
                                                color={"greySecondary"}
                                                startIcon={<IconFolder size={16} />}
                                                onClick={(e) => {
                                                    onClickGroupingTypeTab(GROUPING_VIEW_TYPE.FOLDER);
                                                }}
                                                disabled={!FOLDERS_PERMISSIONS.READ}
                                            >
                                                {t(`common::label::Folders`, "Folders")}
                                            </Button>
                                        )}
                                    </VersionedComponent>
                                    {resourceType === "Asset" && enableCategoryView && (
                                        <Button
                                            fullWidth
                                            variant={
                                                groupingType === GROUPING_VIEW_TYPE.CATEGORY ? "contained" : "outlined"
                                            }
                                            color={"greySecondary"}
                                            startIcon={<IconCategory size={16} />}
                                            onClick={(e) => {
                                                onClickGroupingTypeTab(GROUPING_VIEW_TYPE.CATEGORY);
                                            }}
                                            sx={{
                                                whiteSpace: `nowrap`,
                                            }}
                                            disabled={!CATEGORIES_PERMISSIONS.READ}
                                        >
                                            {t(`common::label::Categories`, "Categories")}
                                        </Button>
                                    )}
                                </ButtonGroup>
                            </Grid>
                        ) : (
                            <Grid container flexWrap={"nowrap"} alignItems={"center"} py={1.5}>
                                {/* 뷰 타입 변경 버튼 */}
                                <ViewSelector activeView={activeView} onClickViewType={updateActiveView} />

                                {/* //뷰 타입 변경 버튼 */}
                                <Box
                                    sx={{
                                        display: `flex`,
                                        alignItems: `center`,
                                        justifyContent: `center`,
                                        width: `100%`,
                                        height: 32,
                                        borderRadius: 1,
                                        textAlign: `center`,
                                        fontSize: theme.typography.body1.fontSize,
                                        color: theme.palette.secondary.contrastText,
                                        backgroundColor: theme.palette.secondary.main,
                                    }}
                                >
                                    <IconCategory size={16} htmlColor={"#fff"} />
                                    <Typography variant="body1_400">
                                        {t(`common::label::Categories`, "Categories")}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                    <Divider flexItem />
                    <Grid item>
                        {/* 검색 영역 */}
                        {compareVersions(stage.version, VERSION_CM_8339_FOLDER_VIEW) < 0 ||
                        groupingType === GROUPING_VIEW_TYPE.CATEGORY ? (
                            <TreeSearchBar
                                name={"Category"}
                                search={search}
                                reset={categoryTreeView.filter.isFilter}
                                onHandleTreeFilter={onClickCategoryTreeFilter}
                                onChangeSearchValue={onChangeSearchValue}
                                onClickResetTreeFilter={onClickResetCategoryTreeFilter}
                            />
                        ) : (
                            <TreeSearchBar
                                name={"Folder"}
                                search={search}
                                reset={folderTreeView.isSearch}
                                selectedFolder={folderTreeView.folderList.selectedFolder.folder}
                                onHandleTreeFilter={onClickFolderTreeFilter}
                                onChangeSearchValue={onChangeSearchValue}
                                onClickResetTreeFilter={onHandleClickRefresh}
                            />
                        )}
                    </Grid>
                    <Grid item xs sx={{ overflow: `auto`, paddingTop: 1 }}>
                        {groupingType === GROUPING_VIEW_TYPE.FOLDER && (
                            <FolderTreeView
                                domain={driveDomain}
                                driveInfo={driveInfo}
                                driveList={folderTreeView.driveList}
                                folderList={folderTreeView.folderList}
                                onHandleCreateFolder={onClickCreateNewFolder}
                                onClickForderTree={onClickFolder}
                                resourceType={resourceType}
                                onRefresh={onRefresh}
                                useActionMenus={!isCms}
                                isCustomMenu={isCustomMenu}
                                isCms={isCms}
                            />
                        )}
                        {groupingType === "category" && (
                            <CategoryTreeView
                                resourceType={resourceType}
                                searchQueryParams={searchQueryParams}
                                onClickTreeItem={onClickTreeItem}
                                onRefresh={onRefresh}
                            />
                        )}
                    </Grid>
                </Grid>
            </Box>
        </Fragment>
    );
};

TreeViewLayout.propTypes = {
    resourceType: PropTypes.string,
    onDropTreeItem: PropTypes.func,
    onClickTreePath: PropTypes.func,
    onHandleFolderView: PropTypes.func,
};
export default TreeViewLayout;
